import React, {
  FC,
  MutableRefObject,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { useButton, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import noteIcon from '../../../../../Assets/icons/table-note.svg';
import arrowForwardIcon from '../../../../../Assets/icons/arrow_forward.svg';
import useSessionActive from '../../../Hooks/useSessionActive';
import CommentModal from './CommentModal';

/** Same box as the promo code row in the cart, so it reads as one family. */
const Row = styled.div`
  /* Sits a step apart from the last modification group above it. */
  margin-top: 16px;
  padding: 20px 22px;
  border-radius: 18px;
  border: 2px solid var(--eposBorderColor);
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  user-select: none;
  color: var(--eposText);
  transition: transform 0.15s ease-out;

  &.is-touching {
    transform: scale(0.98);
  }
`;

const Label = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 19px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* A written comment is quoted and quieter than the prompt. */
  &.has-comment {
    font-weight: 600;
    opacity: 0.75;
  }
`;

interface Props {
  /** Where the comment lives - ItemDetail reads it at commit. Prefilled when editing a line. */
  commentRef: MutableRefObject<string>;
}

/**
 * "Leave a comment..." at the bottom of the details page. Tapping opens
 * CommentModal; the saved text is written to `commentRef` and painted into
 * the row through a DOM ref, so neither typing nor saving re-renders the pane.
 */
const CommentField: FC<Props> = ({ commentRef }) => {
  const { i18n } = useI18n();
  const logger = useLogger('epos-item-comment');
  const labelRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    logger.info('Comment modal opened');
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => setOpen(false), []);

  /** The idle timeout (or start over) must not leave the modal up for the next shopper. */
  const sessionActive = useSessionActive();
  useEffect(() => {
    if (!sessionActive) {
      logger.info('Session ended - closing the comment modal');
      setOpen(false);
    }
  }, [sessionActive]);

  /** Store and paint - no state involved. */
  const handleSave = useCallback(
    (comment: string) => {
      commentRef.current = comment;
      const label = labelRef.current;
      if (label) {
        label.textContent = comment ? `“${comment}”` : i18n('Express.Comment.Leave');
        label.classList.toggle('has-comment', Boolean(comment));
      }
      logger.info('Comment saved', { length: comment.length });
      setOpen(false);
    },
    [commentRef, i18n],
  );

  const { touching, disabled, disabledToast, ...rowButton } = useButton({ onClick: handleOpen });
  const initial = commentRef.current;

  return (
    <>
      <Row
        {...rowButton}
        role="button"
        className={touching ? 'is-touching' : undefined}
        data-testid="epos-comment-row">
        <Icon src={noteIcon} color="var(--eposText)" width="22px" height="22px" />
        <Label ref={labelRef} className={initial ? 'has-comment' : undefined}>
          {initial ? `“${initial}”` : i18n('Express.Comment.Leave')}
        </Label>
        <Icon src={arrowForwardIcon} color="var(--eposText)" width="18px" height="18px" />
      </Row>
      <CommentModal
        open={open}
        defaultValue={commentRef.current}
        onSave={handleSave}
        onClose={handleClose}
      />
    </>
  );
};

export default memo(CommentField);
